/**
 * Adapter shim — Anvil's `src/api.ts` surface re-implemented over Foundry's
 * project-scoped `CrucibleClient`. See docs/anvil-foundry-design-tab-tdd.md §5.
 *
 * The ported Design store/components import these functions in the same shape
 * as Anvil's `api` object; the only difference is that Anvil's leading
 * `projectPath` argument is replaced by `gameId` (the renderer never knows a
 * filesystem path — the main process resolves `<gamePath>/design/`).
 *
 * crucible#3129 — every function below takes the caller's scoped
 * `CrucibleClient` as its FIRST argument (via `useCrucibleClient()` in the
 * owning component, threaded down through any plain helper modules) instead
 * of reaching for the raw unscoped `window.crucible` bridge. Every
 * `design:*` / `game:suggest-name` channel these functions call is
 * `scope: 'workspace'` in `project-channel-metadata.ts`, so an unscoped
 * invoke throws "Project authority is required for <channel>." — this file
 * predates the project-authority gate (#3048) and was never converted (the
 * same-day follow-up `e2e3d7e4d` only converted the Art tab's consumers).
 *
 * No Electron / Tauri imports — this file talks only to the typed
 * `CrucibleClient`, so it satisfies crucible-ui's no-electron lint.
 *
 * Phase 0 implements the project + section subset; later phases fill in
 * screens / flow / tunables / lenses / AI (their channels already exist in
 * the channel map).
 */
import type {
  BuildUpdateStatus,
  DependencyGraph,
  DesignAiChatRequest,
  DesignChangeSummary,
  DesignExportFoundryPackageResult,
  DesignProfile,
  DesignProject,
  DesignLevelCaptureResult,
  DesignJudgeResult,
  DesignReviseResult,
  DesignReconcileResult,
  DesignReconcileStatus,
  DocKind,
  Lens,
  LensFull,
  LevelEntry,
  ReferenceMeta,
  Screen,
  SectionMeta,
} from '@volley/crucible-ipc-types';
import type { DesignSystemRecord } from '@volley/crucible/core/design-system-record.js';
import type { CrucibleClient } from '@volley/crucible-client';

// ---- Project ----

export function openProject(client: CrucibleClient, gameId: string): Promise<DesignProject> {
  return client.invoke('design:openProject', { gameId });
}

/**
 * crucible#2968 — read back the create-time design agent's persisted total
 * identity (palette / typography / art-style / mood), when one was generated
 * for this game. `null` for a legacy/manual create with no design agent run.
 * Callers fold this into their drafting prompts so wizard/sync-generated
 * content stays consistent with the SAME identity the real build + Art tab use.
 */
export function readDesignSystem(
  client: CrucibleClient,
  gameId: string,
): Promise<DesignSystemRecord | null> {
  return client.invoke('design:readDesignSystem', { gameId });
}

export function writeProjectMeta(
  client: CrucibleClient,
  gameId: string,
  patch: {
    pillars?: string[];
    sectionItemMode?: { id: string; itemMode: boolean };
    profile?: Partial<DesignProfile>;
    name?: string;
  },
): Promise<DesignProject> {
  return client.invoke('design:writeProjectMeta', { gameId, patch });
}

/** Toggle a section between the item-based editor and freeform-document mode. */
export function setSectionItemMode(
  client: CrucibleClient,
  gameId: string,
  sectionId: string,
  itemMode: boolean,
): Promise<DesignProject> {
  return writeProjectMeta(client, gameId, { sectionItemMode: { id: sectionId, itemMode } });
}

/** Merge a partial game profile (genre / players / input / art style). */
export function writeProjectProfile(
  client: CrucibleClient,
  gameId: string,
  profile: Partial<DesignProfile>,
): Promise<DesignProject> {
  return writeProjectMeta(client, gameId, { profile });
}

/** Rename the design (project.json name + dashboard displayName). */
export function renameProject(
  client: CrucibleClient,
  gameId: string,
  name: string,
): Promise<DesignProject> {
  return writeProjectMeta(client, gameId, { name });
}

/** Suggest a game name from a concept (reuses the create-flow handler). */
export function suggestGameName(
  client: CrucibleClient,
  conceptText: string,
): Promise<{ displayName: string }> {
  return client.invoke('game:suggest-name', { conceptText });
}

// ---- Sections ----

export function readSection(
  client: CrucibleClient,
  gameId: string,
  filename: string,
): Promise<string> {
  return client.invoke('design:readSection', { gameId, filename });
}

/** Last-built (snapshot) body for a section — for item-level "edited since
 *  last build" pips. Resolves null when there's no snapshot yet. */
export function getBuildSnapshotSection(
  client: CrucibleClient,
  gameId: string,
  filename: string,
): Promise<string | null> {
  return client.invoke('design:getBuildSnapshotSection', { gameId, filename });
}

export function writeSection(
  client: CrucibleClient,
  gameId: string,
  filename: string,
  body: string,
): Promise<void> {
  return client.invoke('design:writeSection', { gameId, filename, body });
}

export function addSection(
  client: CrucibleClient,
  gameId: string,
  title: string,
  doc: DocKind,
): Promise<SectionMeta> {
  return client.invoke('design:addSection', { gameId, title, doc });
}

export function deleteSection(
  client: CrucibleClient,
  gameId: string,
  sectionId: string,
): Promise<void> {
  return client.invoke('design:deleteSection', { gameId, sectionId });
}

// ---- Screens ----

export function listScreens(client: CrucibleClient, gameId: string): Promise<Screen[]> {
  return client.invoke('design:listScreens', { gameId });
}

export function readScreen(
  client: CrucibleClient,
  gameId: string,
  screenId: string,
): Promise<Screen> {
  return client.invoke('design:readScreen', { gameId, screenId });
}

export function writeScreen(
  client: CrucibleClient,
  gameId: string,
  screen: Screen,
  svg: string,
): Promise<Screen> {
  return client.invoke('design:writeScreen', { gameId, screen, svg });
}

export function deleteScreen(
  client: CrucibleClient,
  gameId: string,
  screenId: string,
): Promise<void> {
  return client.invoke('design:deleteScreen', { gameId, screenId });
}

// ---- Levels ----

export function listLevels(client: CrucibleClient, gameId: string): Promise<LevelEntry[]> {
  return client.invoke('design:listLevels', { gameId });
}

export function readLevel(
  client: CrucibleClient,
  gameId: string,
  levelId: string,
): Promise<LevelEntry> {
  return client.invoke('design:readLevel', { gameId, levelId });
}

export function writeLevel(
  client: CrucibleClient,
  gameId: string,
  level: LevelEntry,
): Promise<LevelEntry> {
  return client.invoke('design:writeLevel', { gameId, level });
}

export function deleteLevel(
  client: CrucibleClient,
  gameId: string,
  levelId: string,
): Promise<void> {
  return client.invoke('design:deleteLevel', { gameId, levelId });
}

export function readThreeCs(client: CrucibleClient, gameId: string): Promise<unknown> {
  return client.invoke('design:readThreeCs', { gameId });
}

export function writeThreeCs(
  client: CrucibleClient,
  gameId: string,
  profile: unknown,
): Promise<unknown> {
  return client.invoke('design:writeThreeCs', { gameId, profile });
}

export function readPrevisSkin(client: CrucibleClient, gameId: string): Promise<unknown> {
  return client.invoke('design:readPrevisSkin', { gameId });
}

export function writePrevisSkin(
  client: CrucibleClient,
  gameId: string,
  skin: unknown,
): Promise<unknown> {
  return client.invoke('design:writePrevisSkin', { gameId, skin });
}

export function readPrevisImages(
  client: CrucibleClient,
  gameId: string,
  refs: string[],
): Promise<Record<string, string>> {
  return client.invoke('design:readPrevisImages', { gameId, refs });
}

export function readBuildRender(client: CrucibleClient, gameId: string): Promise<string> {
  return client.invoke('design:readBuildRender', { gameId });
}

export function readProgression(client: CrucibleClient, gameId: string): Promise<unknown> {
  return client.invoke('design:readProgression', { gameId });
}

export function writeProgression(
  client: CrucibleClient,
  gameId: string,
  progression: unknown,
): Promise<unknown> {
  return client.invoke('design:writeProgression', { gameId, progression });
}

// ---- Graph ----

export function readGraph(client: CrucibleClient, gameId: string): Promise<DependencyGraph> {
  return client.invoke('design:readGraph', { gameId });
}

export function writeGraph(
  client: CrucibleClient,
  gameId: string,
  graph: DependencyGraph,
): Promise<void> {
  return client.invoke('design:writeGraph', { gameId, graph });
}

// ---- References ----

export function listReferences(client: CrucibleClient, gameId: string): Promise<ReferenceMeta[]> {
  return client.invoke('design:listReferences', { gameId });
}

export function readReference(
  client: CrucibleClient,
  gameId: string,
  filename: string,
): Promise<string> {
  return client.invoke('design:readReference', { gameId, filename });
}

// ---- Design references (bundled Weekend docs, folder-driven, optional) ----

/** List the bundled Weekend design-reference docs (every `*.md` in the
 *  references folder — add/edit/remove freely). */
export function listReferenceDocs(client: CrucibleClient): Promise<ReferenceMeta[]> {
  return client.invoke('design:listReferenceDocs');
}

/** Read one reference doc's body (frontmatter stripped) for prompt context. */
export function readReferenceDoc(client: CrucibleClient, filename: string): Promise<string> {
  return client.invoke('design:readReferenceDoc', { filename });
}

// ---- Lenses (global, not per-game) ----

export function listLenses(client: CrucibleClient): Promise<Lens[]> {
  return client.invoke('design:listLenses');
}

export function readLens(
  client: CrucibleClient,
  id: string,
  kind: 'agent' | 'skill',
): Promise<LensFull> {
  return client.invoke('design:readLens', { id, kind });
}

// ---- AI ----

// ---- Build handoff ----

/** Generate the Foundry build package into a committed `design/<subdir>/`.
 *  First send (`mode: 'full'`) regenerates the full spec; subsequent sends
 *  (`mode: 'update'`) ship a deterministic changelog instead. Returns the
 *  subdir, phase plan, the "Game Plan" markdown shown in the embedded Build
 *  Plan surface, the mode, seeded plan metadata, and (for updates) a
 *  structured change summary. */
export function exportFoundryPackage(
  client: CrucibleClient,
  gameId: string,
): Promise<DesignExportFoundryPackageResult> {
  return client.invoke('design:exportFoundryPackage', { gameId });
}

/** Has this design been sent to build before, and does it have unsent changes?
 *  Drives the design tab's "Send to build" → "Send Build Updates" button. */
export function getBuildUpdateStatus(
  client: CrucibleClient,
  gameId: string,
): Promise<BuildUpdateStatus> {
  return client.invoke('design:getBuildUpdateStatus', { gameId });
}

/** Advance the "last sent" snapshot. Called only when the user CONFIRMS the
 *  send (final button click) — preparing/dismissing the dialog must not move
 *  it, so the button keeps reflecting the real pending changes. */
export function commitBuildSnapshot(
  client: CrucibleClient,
  gameId: string,
  subdir?: string,
): Promise<void> {
  return client.invoke('design:commitBuildSnapshot', { gameId, subdir });
}

// ---- Bi-directional sync (chat → design) ----

/** New chat activity since the design docs were last reconciled from chat.
 *  Drives the "Sync design from chat (N)" control. */
export function getReconcileStatus(
  client: CrucibleClient,
  gameId: string,
): Promise<DesignReconcileStatus> {
  return client.invoke('design:getReconcileStatus', { gameId });
}

/** AI pass: propose design-section edits that reflect recent chat changes. */
export function reconcileFromChat(
  client: CrucibleClient,
  gameId: string,
): Promise<DesignReconcileResult> {
  return client.invoke('design:reconcileFromChat', { gameId });
}

/** Write the accepted section edits + advance the reconcile marker (and the
 *  build snapshot when sections were written). Empty `sections` = dismiss. */
export function applyReconcile(
  client: CrucibleClient,
  gameId: string,
  sections: { filename: string; body: string; original?: string }[],
  chatCount: number,
): Promise<void> {
  return client.invoke('design:applyReconcile', { gameId, sections, chatCount });
}

/** Scan the game runtime for levels the design source hasn't captured (build → design). */
export function captureLevelsFromBuild(
  client: CrucibleClient,
  gameId: string,
): Promise<DesignLevelCaptureResult> {
  return client.invoke('design:captureLevelsFromBuild', { gameId });
}

/** Write accepted captured levels into design/levels/. Returns the written level ids. */
export function applyLevelCapture(
  client: CrucibleClient,
  gameId: string,
  levels: unknown[],
): Promise<{ written: string[] }> {
  return client.invoke('design:applyLevelCapture', { gameId, levels });
}

/** LLM design-judge: ensemble-score one level for fun/fit + concrete fixes (design §23). */
export function judgeLevel(
  client: CrucibleClient,
  gameId: string,
  levelId: string,
): Promise<DesignJudgeResult> {
  return client.invoke('design:judgeLevel', { gameId, levelId });
}

/** Auto-revise loop: judge → LLM rewrite → re-validate → re-judge, proposing an improved
 *  level for the human to apply (non-destructive; design §23). */
export function reviseLevel(
  client: CrucibleClient,
  gameId: string,
  levelId: string,
  maxRounds?: number,
): Promise<DesignReviseResult> {
  return client.invoke('design:reviseLevel', { gameId, levelId, maxRounds });
}

/** Cancel an in-flight build-package export. */
export function cancelExport(client: CrucibleClient, gameId: string): Promise<void> {
  return client.invoke('design:cancelExport', { gameId });
}

/** One-shot chat (no streaming). */
export function aiChat(
  client: CrucibleClient,
  req: Omit<DesignAiChatRequest, 'gameId'> & { gameId: string },
): Promise<{ text: string; usage?: unknown }> {
  return client.invoke('design:aiChat', req);
}

export interface AiStreamCallbacks {
  onDelta?: (text: string, accumulated: number) => void;
  onStatus?: (status: string) => void;
}

export interface AiStreamResult {
  text: string;
  usage?: unknown;
  cancelled?: boolean;
}

/**
 * Streaming chat. Re-creates Anvil's `aiChatStream(args, { onDelta, onStatus })`.
 *
 * ⚠️ Ordering invariant (TDD §5): the renderer MINTS the `requestId` and
 * SUBSCRIBES to `design:aiStream:chunk` BEFORE invoking `design:aiStreamStart`.
 * Push chunks are ephemeral `webContents.send` (no buffering), so any chunk
 * emitted before `.subscribe()` is registered is lost forever. In cloud mode the
 * main process resolves the whole call to one `done` chunk *during* the invoke —
 * so subscribing after the invoke would be a guaranteed miss, not a race. Minting
 * the id client-side lets us register the filtered listener first.
 *
 * Returns a `{ cancel }` handle plus the settling promise.
 */
export function aiChatStream(
  client: CrucibleClient,
  req: Omit<DesignAiChatRequest, 'gameId'> & { gameId: string },
  cb: AiStreamCallbacks = {},
): { requestId: string; cancel: () => void; done: Promise<AiStreamResult> } {
  const requestId =
    globalThis.crypto?.randomUUID?.() ?? `req-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  let settled = false;
  const done = new Promise<AiStreamResult>((resolve, reject) => {
    let acc = '';
    // Register the filtered listener BEFORE kickoff.
    const off = client.subscribe('design:aiStream:chunk', (c) => {
      if (c.requestId !== requestId) return;
      if (c.type === 'delta') {
        acc += c.text ?? '';
        cb.onDelta?.(c.text ?? '', c.accumulated ?? acc.length);
      } else if (c.type === 'status' && c.status === 'cancelled') {
        // 'cancelled' is the ONE terminal status — settle + unsubscribe, else
        // the promise hangs and the listener leaks (TDD §5).
        settled = true;
        off();
        resolve({ text: acc, cancelled: true });
      } else if (c.type === 'status') {
        cb.onStatus?.(c.status ?? '');
      } else if (c.type === 'done') {
        settled = true;
        off();
        resolve({ text: c.text ?? acc, usage: c.usage });
      } else if (c.type === 'error') {
        settled = true;
        off();
        reject(new Error(c.error ?? 'stream error'));
      }
    });

    // Kick off only after the listener exists. If the invoke itself rejects,
    // tear the listener down.
    client.invoke('design:aiStreamStart', { ...req, requestId }).catch((e: unknown) => {
      if (settled) return;
      settled = true;
      off();
      reject(e instanceof Error ? e : new Error(String(e)));
    });
  });

  const cancel = (): void => {
    if (settled) return;
    void client.invoke('design:aiStreamCancel', { requestId });
  };

  return { requestId, cancel, done };
}
